import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Lock } from 'lucide-react';
import { supabase } from '../supabaseClient';

export const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hasRecoverySession, setHasRecoverySession] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const syncRecoveryState = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!isMounted) {
                return;
            }

            setHasRecoverySession(Boolean(session));
            setLoading(false);
        };

        void syncRecoveryState();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (!isMounted) {
                return;
            }

            if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                setHasRecoverySession(Boolean(session));
                setLoading(false);
            }

            if (event === 'SIGNED_OUT') {
                setHasRecoverySession(false);
            }
        });

        return () => {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if (password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (!hasRecoverySession) {
            setError('Open the password reset link from your email again, then try setting a new password.');
            return;
        }

        setSubmitting(true);

        try {
            const { error: updateError } = await supabase.auth.updateUser({
                password,
            });

            if (updateError) {
                throw updateError;
            }

            setSuccess(true);
            setPassword('');
            setConfirmPassword('');

            await supabase.auth.signOut();
            window.setTimeout(() => {
                navigate('/login', { replace: true });
            }, 1200);
        } catch (submitError) {
            const message = submitError instanceof Error ? submitError.message : 'Failed to reset password.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-linear-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4 overflow-hidden relative">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                    className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 w-full max-w-md relative z-10"
            >
                <div className="flex items-center gap-4 mb-6">
                    <img
                        src="/logo.png"
                        alt="AR Drawing Master"
                        className="w-14 h-14 rounded-2xl shadow-lg object-cover"
                        width={56}
                        height={56}
                    />
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-indigo-500 dark:text-indigo-300">
                            Secure Reset
                        </p>
                        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Set New Password</h1>
                    </div>
                </div>

                <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 mb-6"
                >
                    <ArrowLeft size={16} />
                    Back to Sign In
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Choose a new password for your account.
                </p>

                {loading ? (
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 text-sm text-gray-500 dark:text-gray-400">
                        Verifying your reset link...
                    </div>
                ) : success ? (
                    <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 p-5 text-emerald-700 dark:text-emerald-300 flex items-start gap-3">
                        <CheckCircle2 className="shrink-0 mt-0.5" size={20} />
                        <div>
                            <p className="font-bold">Password updated</p>
                            <p className="text-sm">Redirecting you to sign in...</p>
                        </div>
                    </div>
                ) : !hasRecoverySession ? (
                    <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 p-5 text-amber-700 dark:text-amber-300">
                        <p className="font-bold mb-1">Reset link required</p>
                        <p className="text-sm">
                            Open the password reset link from your email on this device to finish changing your password.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">New Password</label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                    placeholder="Enter a new password"
                                    autoComplete="new-password"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 ml-1">Confirm Password</label>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-hidden"
                                    placeholder="Repeat your new password"
                                    autoComplete="new-password"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-xl text-sm"
                            >
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-lg shadow-lg shadow-indigo-500/30 hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Updating...' : 'Update Password'}
                        </button>
                    </form>
                )}
            </motion.div>
        </div>
    );
};
